import FormCheckbox from '@form/FormCheckbox';
import useAuth from '@hooks/useAuth';
import useModalManager from '@hooks/useModalManager';
import { Blockquote, StyledCheckbox } from '@ui';
import { useClubSlug, useSettings } from 'components/Clubs/hooks';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { PaymentModuleKey } from '../types';
import MandateSelection from './MandateSelection';
import useMandates from './useMandates';

interface MollieConnectMandatesProps {
    className?: string;
    module?: PaymentModuleKey;
}

export default function MollieConnectMandates({ className, module }: MollieConnectMandatesProps) {
    const { t } = useTranslation('general');
    const { isAuthenticated } = useAuth();
    const { watch, setValue } = useFormContext();
    const clubSlug = useClubSlug();
    const [unselectedRecommendation, setUnselectedRecommendation] = useState(false);
    const mandate = watch('mollie_connect_create_mandate');
    const mandateId = watch('mollie_connect_mandate_id');
    const [isMandateAllowed] = useSettings('mollie.mandate.allow', clubSlug);
    const { data } = useMandates(
        clubSlug,
        {
            onSuccess: () => {
                // if no mandate id is set, but mandates are available, select the first one
                if (!mandateId && data?.length > 0 && !unselectedRecommendation) {
                    setValue('mollie_connect_mandate_id', data[0].id);
                    setValue('mollie_connect_create_mandate', false);
                }
            }
        },
        module
    );
    const { addModal, removeModal } = useModalManager();

    const handleOpenMandateSelection = () => {
        addModal({
            title: t('mollie.mandates.selectModalTitle'),
            content: (id) => (
                <MandateSelection
                    onSelectMandate={(mandateId) => {
                        if (mandateId) {
                            setValue('mollie_connect_create_mandate', false);
                        }

                        setValue('mollie_connect_mandate_id', mandateId);
                        removeModal(id);
                    }}
                    selectedMandateId={mandateId}
                    createMandate={mandate}
                    module={module}
                />
            )
        });
    };

    // resets mandate id when creating a new mandate
    useEffect(() => {
        if (mandate) {
            setValue('mollie_connect_mandate_id', undefined);
        }
    }, [mandate, setValue]);

    if (!isAuthenticated || !isMandateAllowed) return null;

    return (
        <div className={className}>
            <FormCheckbox
                name="mollie_connect_create_mandate"
                disabled={!!mandateId}
                label={t('mollie.mandates.saveCheckbox')}
            />
            {data?.length > 0 && (
                <StyledCheckbox
                    onChange={(value) => {
                        if (value) {
                            handleOpenMandateSelection();
                        } else {
                            setUnselectedRecommendation(true);
                            setValue('mollie_connect_mandate_id', undefined);
                        }
                    }}
                    isSelected={!!mandateId}
                    label={t('mollie.mandates.useSavedCheckbox')}
                />
            )}
            {!!mandate && (
                <Blockquote color="warning" className="tw-mt-2">
                    <Trans t={t} i18nKey="mollie.mandates.hint" components={{ b: <b /> }} />
                </Blockquote>
            )}
        </div>
    );
}
